import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/hooks/global-tools';
import useSearchRelatedApprovals from '@/hooks/approval/useSearchRelatedApprovals';
import useClickOutside from '@/components/Record/SubjectContent/@hooks/useClickOutside';
import useTabOutClose from '@/components/Approval/@hooks/useTabOutClose';
import SearchBar from '@/components/Approval/RelatedApproval/SearchBar';
import ResultLayer from '@/components/Approval/RelatedApproval/ResultLayer';
import { Root as SearchRoot } from '@/components/Approval/RelatedApproval/styles';
import { getApprovalDocUrl } from '@/utils/urls';
import type {
  RelatedApprovalItem,
  RelatedApprovalRelationType,
} from '@/types/approval';

/** 연관품의 편집 모드 검색 (조회 화면 한정) */
const MIN_KEYWORD_LENGTH = 2;
const MAX_KEYWORD_LENGTH = 300;
const SEARCH_DEBOUNCE_MS = 500;
const FOCUS_FIRST_DELAY_MS = 50;

const TYPE_PLACEHOLDER: Record<RelatedApprovalRelationType, string> = {
  parents: '상위 품의의 제목이나 링크를 2글자 이상 입력해 주세요.',
  children: '하위 품의의 제목이나 링크를 2글자 이상 입력해 주세요.',
  related: '관련 품의의 제목이나 링크를 2글자 이상 입력해 주세요.',
};

type RelatedSearchProps = {
  type: RelatedApprovalRelationType;
  /** 결과에서 제외해야 하는 docId 들 — 현재 문서 docId + 이미 연결된 docId + 본 세션 선택 docId. */
  ignoreDocIds: Set<string>;
  /** 결과 클릭/Enter 시 호출. 이미 선택된 docId 는 본 컴포넌트에서 alert 후 무시. */
  onSelect: (item: RelatedApprovalItem) => void;
};

const RelatedSearch = ({
  type,
  ignoreDocIds,
  onSelect,
}: RelatedSearchProps) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();

  const [keyword, setKeyword] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const passesMinLength = keyword.trim().length >= MIN_KEYWORD_LENGTH;

  const { items, isLoading, error } = useSearchRelatedApprovals(
    passesMinLength ? keyword : '',
    { debounceMs: SEARCH_DEBOUNCE_MS },
  );

  // 백엔드가 ignoreDocumentIds 미지원 → 충돌 docId 는 클라이언트에서 필터.
  const visibleResults = useMemo(
    () => items.filter((it) => !ignoreDocIds.has(it.docId)),
    [items, ignoreDocIds],
  );

  const closeLayer = useCallback(() => setIsOpen(false), []);
  useClickOutside(containerRef, closeLayer);
  const handleSectionBlur = useTabOutClose<HTMLDivElement>(isOpen, closeLayer);

  const focusFirstResult = useCallback(() => {
    setTimeout(() => {
      const first =
        containerRef.current?.querySelector<HTMLElement>('[data-result-item]');
      first?.focus();
    }, FOCUS_FIRST_DELAY_MS);
  }, []);

  const handleChange = useCallback(
    (value: string) => {
      if (value.length > MAX_KEYWORD_LENGTH) {
        showError({ message: t('최대 300자까지 입력 가능합니다.') });
        return;
      }
      setKeyword(value);
      // 2글자 미만 — layer 닫음(호출/표시 모두 X).
      setIsOpen(value.trim().length >= MIN_KEYWORD_LENGTH);
    },
    [showError, t],
  );

  const handleClear = useCallback(() => {
    setKeyword('');
    setIsOpen(false);
  }, []);

  const handleSelect = useCallback(
    (item: RelatedApprovalItem) => {
      if (ignoreDocIds.has(item.docId)) {
        showError({ message: t('이미 선택된 품의입니다.') });
        return;
      }
      onSelect(item);
      // 선택 후 input 비움 + 결과 layer 닫음 — 다음 검색 바로 가능.
      setKeyword('');
      setIsOpen(false);
      inputRef.current?.focus();
    },
    [ignoreDocIds, onSelect, showError, t],
  );

  const handleOpenInNewWindow = useCallback((docId: string) => {
    if (typeof window === 'undefined') return;
    // windowFeatures 를 비워 일반 새 탭 — feature 문자열은 팝업 간주/차단될 수 있음. noopener 는 opener 끊기로.
    const win = window.open(getApprovalDocUrl(docId), '_blank');
    if (win) win.opener = null;
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // 한글 IME 조합 중 keydown 은 무시 — 자모 중복 commit 이슈 회피
      if (e.nativeEvent.isComposing || e.keyCode === 229) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        handleClear();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (isOpen && visibleResults.length > 0) {
          handleSelect(visibleResults[0]);
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        if (isOpen && visibleResults.length > 0) {
          e.preventDefault();
          focusFirstResult();
        }
      }
    },
    [isOpen, visibleResults, handleSelect, handleClear, focusFirstResult],
  );

  const handleResultItemKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = e.currentTarget.nextElementSibling as HTMLElement | null;
        next?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = e.currentTarget
          .previousElementSibling as HTMLElement | null;
        if (prev && prev.matches('[data-result-item]')) {
          prev.focus();
        } else {
          inputRef.current?.focus();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleClear();
        inputRef.current?.focus();
      }
    },
    [handleClear],
  );

  return (
    <SearchRoot ref={containerRef} onBlur={handleSectionBlur}>
      <SearchBar
        ref={inputRef}
        placeholder={t(TYPE_PLACEHOLDER[type])}
        value={keyword}
        onChange={handleChange}
        onClear={handleClear}
        onKeyDown={handleKeyDown}
      />
      <ResultLayer
        isOpen={isOpen && passesMinLength}
        items={visibleResults}
        isLoading={passesMinLength && isLoading}
        hasError={!!error}
        onSelect={handleSelect}
        onOpenInNewWindow={handleOpenInNewWindow}
        onItemKeyDown={handleResultItemKeyDown}
      />
    </SearchRoot>
  );
};

export default RelatedSearch;
